import React, {
	memo,
	useCallback,
	useEffect,
	useMemo
	// useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Accordion,
	AccordionTitle,
	Form,
	Header,
	Icon,
	Message
} from 'semantic-ui-react';

import useFormErrors from './utils/useFormErrors';

import DynamicFormField from './DynamicFormField';

import styles from './document-modal.module.css';
import FormErrors from '../Shared/FormErrors';

const DynamicForm = ( {
	admin,
	formData,
	instructions,
	sections,
	setModuleErrors,
	ui,
	updateField,
	validate
} ) => {
	const {
		clearFormErrors,
		formErrors,
		setFormErrors,
		validateFormErrors,
		handleValidation
	} = useFormErrors();

	// const [ sectionIndex, setSectionIndex ] = useState( 0 );

	// const handleAccordion = useCallback( ( e, titleProps ) => {
	// 	const { index } = titleProps;

	// 	setSectionIndex( prev => {
	// 		if ( prev === index ) {
	// 			return -1;
	// 		}

	// 		return index;
	// 	} );
	// }, [] );

	useEffect( () => {
		if ( validateFormErrors() ) {
			setModuleErrors( true );
		} else {
			setModuleErrors( false );
		}
	}, [
		setModuleErrors,
		validateFormErrors
	] );

	const rows = useCallback( section => {
		const filteredUi = ui.filter( ( { sectionId } ) => sectionId === section );

		const rows = filteredUi.reduce(
			(
				acc,
				field,
				i,
				arr
			) => {
				if (
					i === 0 // first field
					|| field.layout.newLine // newline is indicated
					|| [ 1, 4 ].includes( field.type ) // before paragraphs & group numeric
					|| [ 1, 4 ].includes( arr[ i - 1 ].type ) // after paragraphs & group numeric
				) {
					return [
						...acc,
						[ field ]
					];
				}

				const lastIndex = acc.length - 1;

				return [
					...acc.slice( 0, lastIndex ),
					[
						...acc[ lastIndex ],
						field
					]
				];
			},
			[]
		);

		const fieldErrors = [];

		const fields = rows.map( ( row, i ) => (
			<Form.Group
				key={ i }
				style={ { display: 'flex' } }>
				{
					row.map( field => {
						if (
							( field.visibility === 'admin' && !admin )
							|| ( field.visibility === 'agency' && admin )
						) {
							return null;
						}

						for ( const { type, id, optionID, value } of field.condition ?? [] ) {
							if ( type === 1 ) {
								let key = optionID ? `${ id }_${ optionID }` : id;

								if (
									Array.isArray( formData[ key ] ) && !formData[ key ].includes( value )
									&& formData[ key ] !== value
								) {
									return null;
								}
							}
						}

						switch ( field.type ) {
							case 4: { // group numeric
								const mapFieldOptions = field.options.map( ( { id: qId, label } ) => {
									const optionId = `${ field.id }_${ qId }`;
									const optionName = `${ field.name }_${ qId }`;
									const optionField = {
										...field,
										id: optionId,
										name: optionName,
										label
									};
									const errors = handleValidation( {
										data: formData[ optionName ],
										name: optionName,
										label,
										validation: field.validation
									} );

									fieldErrors.push( errors );

									return (
										<DynamicFormField
											key={ optionId }
											data={ formData[ optionName ] }
											errors={ errors }
											field={ optionField }
											forceValidation={ validate }
											updateField={ updateField }
										/>
									);
								} );

								return (
									<Form.Field key={ field.id }>
										<label>{ field.label }</label>
										<Form.Group
											style={ {
												display: 'flex',
												flexWrap: 'wrap',
												marginBottom: 0
											} }
										>
											{ mapFieldOptions() }
										</Form.Group>
									</Form.Field>
								);
							}

							default: {
								const data = formData[ field.id ];
								const errors = handleValidation( {
									data,
									label: field.label,
									validation: field.validation
								} );

								fieldErrors.push( errors );

								return (
									<DynamicFormField
										key={ field.id }
										data={ formData[ field.id ] }
										errors={ errors }
										field={ field }
										forceValidation={ validate }
										updateField={ updateField }
									/>
								);
							}
						}
					} )
				}
			</Form.Group>
		) );

		setFormErrors( prev => [ ...prev, ...fieldErrors ] );

		return fields;
	}, [
		admin,
		formData,
		handleValidation,
		setFormErrors,
		ui,
		updateField,
		validate
	] );

	const renderSections = useMemo( () => {
		clearFormErrors();

		const panels = sections.map( ( {
			id,
			name,
			description,
			condition,
			visibility
		} ) => {
			if (
				( visibility === 'admin' && !admin )
				|| ( visibility === 'agency' && admin )
			) {
				return null;
			}

			for ( const { type, id, optionID, value } of condition ) {
				if ( type === 1 ) {
					let key = optionID ? `${ id }_${ optionID }` : id;

					if (
						( Array.isArray( formData[ key ] ) && !formData[ key ].includes( value ) )
						&& formData[ key ] !== value
					) {
						return null;
					}
				}
			}

			return {
				key: id,
				title: <AccordionTitle>
					<Header><Icon name="dropdown" />{ name }</Header>
				</AccordionTitle>,
				content: <>
					<p>{ description }</p>
					{ rows( id ) }
				</>
			};
		} );

		return (
			<>
				{ rows( 0 ) }
				<Accordion
					exclusive={ false }
					fluid
					panels={ panels }
				>

				</Accordion>
			</>
		);
	}, [
		admin,
		clearFormErrors,
		formData,
		rows,
		sections
	] );

	const mapFormErrors = useCallback(
		() => Object.values( formErrors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ formErrors ]
	);

	const hiddenInputStyles = useMemo( () => ( {
		opacity: 0,
		position: 'absolute',
		pointerEvents: 'none'
	} ), [] );

	return (
		<div className={ styles.formContainer }>
			<Form
				autoComplete="off"
				error={ validate && validateFormErrors() }
			>
				{ instructions
					? <div className={ styles.instructions }>
						<h3>Instructions</h3>
						<div dangerouslySetInnerHTML={ { __html: instructions } } />
					</div>
					: null
				}
				<input
					name="name"
					style={ hiddenInputStyles }
				/>
				{ renderSections }
				<FormErrors mapFormErrors={ mapFormErrors } />
			</Form>
		</div>
	);
};

DynamicForm.propTypes = {
	admin: PropTypes.bool,
	formData: PropTypes.object.isRequired,
	instructions: PropTypes.string,
	sections: PropTypes.array.isRequired,
	setModuleErrors: PropTypes.func.isRequired,
	ui: PropTypes.array.isRequired,
	updateField: PropTypes.func.isRequired,
	validate: PropTypes.bool
};

export default memo( DynamicForm );
